import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { Language, MenuLayout, PagedResult, UserRef } from '@/shared/api/types';

/*
 * Admin API (`/api/admin`, docs/modules/admin.md) plus the endpoints of other modules the admin panel uses: the search
 * index (`/api/search/stats`, `/api/search/reindex`) and the cleanup of unattached uploads (`/api/files/cleanup`,
 * docs/modules/files.md). Query keys start with 'admin'.
 */

/** `GET /api/admin/settings/public` — the shell reads the same query (key below) to apply name, logo and accent. */
export interface PublicPortalSettings {
  companyName: string;
  logoUrl: string | null;
  accentColor: string | null;
  defaultLanguage: Language;
  defaultTimeZone: string;
  /** The default arrangement of the left menu for everybody; null: the natural order. */
  menu?: MenuLayout | null;
}

export interface PortalSettings {
  companyName: string;
  logoFileId: string | null;
  logoUrl: string | null;
  accentColor: string | null;
  defaultLanguage: Language;
  defaultTimeZone: string;
  updatedAt: string;
  updatedBy: UserRef | null;
}

export interface UpdatePortalSettings {
  companyName: string;
  logoFileId: string | null;
  accentColor: string | null;
  defaultLanguage: Language;
  defaultTimeZone: string;
}

export interface DependencyHealth {
  name: string;
  status: 'Healthy' | 'Degraded' | 'Unhealthy';
  description: string | null;
  durationMs: number;
  error: string | null;
  version: string | null;
  details: Record<string, string>;
}

export interface RuntimeInfo {
  applicationVersion: string | null;
  /** Git commit of the API build; null for a local build. */
  commit: string | null;
  builtAt: string | null;
  /** Heartbeat id of the API process that answered (matches one of `SystemInfo.instances`). */
  instanceId: string;
  framework: string;
  operatingSystem: string;
  environment: string;
  hostRole: string;
  machineName: string;
  processorCount: number;
  startedAt: string;
  uptimeSeconds: number;
  workingSetBytes: number;
  managedMemoryBytes: number;
  serverTime: string;
}

export interface ModuleInfo {
  name: string;
  schema: string;
  tables: number;
  sizeBytes: number;
  migrations: number;
  lastMigration: string | null;
}

/** A running host process (heartbeat in Valkey) with its build. */
export interface HostInstance {
  id: string;
  /** `api`, `worker` or `test` (dev stacks, test hosts). */
  role: string;
  service: string;
  machine: string;
  version: string;
  commit: string | null;
  builtAt: string | null;
  startedAt: string;
  lastSeenAt: string;
  /** The API process that answered this request. */
  isCurrent: boolean;
}

/**
 * One off-site target as the host's health check saw it (the S3 bucket or the Hetzner Storage Box). `pending`: configured less than
 * a day ago, nothing to show yet; `down`: its repository cannot be read; `degraded`: it can, but `problems` lists what is wrong.
 */
export interface BackupTarget {
  kind: 's3' | 'sftp';
  /** The bucket and endpoint, or the account and box: no secret. */
  name: string;
  state: 'ok' | 'pending' | 'degraded' | 'down';
  /** When the newest WAL file reached this target; null when unknown. */
  lastWalAt: string | null;
  /** Finished WAL segments this target lacks; null when unknown. */
  walSegmentsMissing: number | null;
  lastBackupAt: string | null;
  /** `full` or `diff`. */
  lastBackupType: string | null;
  lastFullBackupAt: string | null;
  /** Codes: unreachable, stanza-missing, backup-missing, archive-behind, backup-stale, full-stale. */
  problems: string[];
}

/**
 * Backups and point-in-time recovery of the database. `skipped`: the off-site storage was postponed on purpose (every copy of the
 * data is on the server); `unconfigured`: nothing set up (development); `degraded`: configured, but `problems` lists what is wrong.
 * `storage` names the targets that are configured: an S3 bucket, a Hetzner Storage Box (`sftp`), or both (`s3+sftp`).
 */
export interface BackupStatus {
  state: 'active' | 'degraded' | 'skipped' | 'unconfigured';
  storage: 's3' | 'sftp' | 's3+sftp' | 'skipped' | 'none';
  archiveEnabled: boolean;
  lastArchivedAt: string | null;
  lastArchivedWal: string | null;
  failedCount: number;
  lastFailedAt: string | null;
  walSegmentsWaiting: number;
  lastBackupAt: string | null;
  /** `full` or `diff`. */
  lastBackupType: string | null;
  lastFullBackupAt: string | null;
  /**
   * Codes: skipped, unconfigured, unreadable, archive-off, archive-failing, archive-stale, backup-missing, backup-stale, full-stale,
   * target-down, target-degraded, targets-stale.
   */
  problems: string[];
  /** Each configured target on its own; empty from an older API or a stack without the production tooling. */
  targets?: BackupTarget[];
  /** When the host's health check wrote `targets`. */
  targetsCheckedAt?: string | null;
}

export interface SystemInfo {
  status: 'Healthy' | 'Degraded' | 'Unhealthy';
  dependencies: DependencyHealth[];
  runtime: RuntimeInfo;
  databaseName: string;
  databaseSizeBytes: number;
  modules: ModuleInfo[];
  brokerConnections: { name: string; state: string | null; user: string | null }[];
  jobs: { name: string; intervalSeconds: number }[];
  instances: HostInstance[];
  /** False when the heartbeats could not be read (Valkey unavailable): `instances` is empty then. */
  instancesAvailable: boolean;
  backups: BackupStatus;
  /** Open alerts (`/admin/alerts`); absent from an older API. */
  activeAlerts?: number;
}

export interface BrokerOverview {
  rabbitMqVersion: string | null;
  erlangVersion: string | null;
  clusterName: string | null;
  publishRate: number;
  deliverRate: number;
  ackRate: number;
  messages: number;
  messagesReady: number;
  messagesUnacknowledged: number;
  connections: number;
  channels: number;
  queues: number;
  consumers: number;
}

export interface EndpointStatus {
  endpoint: string;
  module: string;
  queue: string;
  isBroadcast: boolean;
  ready: number;
  unacknowledged: number | null;
  consumers: number;
  retryMessages: number;
  errorMessages: number;
  publishRate: number;
  deliverRate: number;
  ackRate: number;
}

export interface OutboxStatus {
  module: string;
  schema: string;
  pending: number;
  scheduled: number;
  failing: number;
  parked: number;
  oldestPendingAt: string | null;
  nextDueAt: string | null;
}

export interface MessagingOverview {
  broker: BrokerOverview | null;
  managementAvailable: boolean;
  endpoints: EndpointStatus[];
  outboxes: OutboxStatus[];
  errorMessages: number;
  retryMessages: number;
  parkedOutboxMessages: number;
  checkedAt: string;
}

export interface MessagingSummary {
  errorMessages: number;
  parkedOutboxMessages: number;
}

export interface ErrorMessage {
  messageId: string;
  messageName: string | null;
  correlationId: string | null;
  sentAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
  exceptionType: string | null;
  exceptionMessage: string | null;
  stackTrace: string | null;
  originalQueue: string | null;
  attempt: number | null;
  contentType: string;
  payload: string;
  payloadSize: number;
  headers: Record<string, string | null>;
}

/** `data`: data left the portal or was removed in bulk (CRM exports, mass deletions, public links of the disk). */
export type AuditCategory = 'admin' | 'security' | 'data';

export interface AuditEntry {
  id: string;
  actor: UserRef | null;
  action: string;
  target: string | null;
  details: string | null;
  createdAt: string;
  /** `admin`: an administrator's action; `security`: a sign-in security event of the identity module. */
  category?: AuditCategory;
  /** For a security event: the account it is about (the actor is who did it: the account itself, or an administrator). */
  subject?: UserRef | null;
  /** For a security event: the client address; `target` is the device. */
  ip?: string | null;
}

/** Filters of the audit page (all optional). */
export interface AuditFilter {
  category?: AuditCategory;
  actions?: string[];
  userId?: string;
  from?: string;
  to?: string;
  /** Case-insensitive text looked for in the target and the details (at most 100 characters). */
  q?: string;
}

/** Entries a CSV export of the journal holds at most (`X-Orbita-Truncated` says when the journal had more). */
export const AUDIT_EXPORT_LIMIT = 50_000;

/** The file of an export of the journal. */
export interface AuditExport {
  blob: Blob;
  fileName: string;
  /** The journal had more entries than the file holds. */
  truncated: boolean;
  /** Entries in the file; null when the server did not say. */
  rows: number | null;
}

export type TwoFactorMode = 'optional' | 'administrators' | 'everyone';

/** `GET/PUT /api/auth/security-policy` (identity module): the sign-in security policy of the portal. */
export interface SecurityPolicy {
  twoFactorMode: TwoFactorMode;
  twoFactorGraceDays: number;
  /** From when the requirement is enforced (the end of the grace period); null while it is optional. */
  twoFactorEnforcedFrom: string | null;
  passwordMinLength: number;
  passwordRequireMixedCase: boolean;
  passwordRequireDigit: boolean;
  passwordRequireSymbol: boolean;
  passwordHistory: number;
  passwordMaxAgeDays: number;
  blockCommonPasswords: boolean;
  lockoutMaxAttempts: number;
  lockoutMinutes: number;
  lockoutWindowMinutes: number;
  /** The most sessions (devices) an account holds; a sign-in past it ends the least recently active ones (0 = no limit). */
  maxSessionsPerUser: number;
  /** Optional allow-list of the administrator role: addresses and CIDR networks it works from (empty = from anywhere). */
  adminNetworks: string[];
  /** `Identity__AdminNetworkBypass` is on: the list is ignored (the emergency break-glass). */
  adminNetworkBypass: boolean;
  /** The address the server sees the caller at (what the list has to cover); null when unknown. */
  yourAddress: string | null;
  updatedAt: string | null;
  updatedBy: UserRef | null;
  coverage: {
    active: number;
    withTwoFactor: number;
    administrators: number;
    administratorsWithTwoFactor: number;
  };
}

export type SecurityPolicyInput = Omit<
  SecurityPolicy,
  'twoFactorEnforcedFrom' | 'updatedAt' | 'updatedBy' | 'coverage' | 'adminNetworkBypass' | 'yourAddress'
>;

/** Documents of one kind (entity type: `message`, `comment`, `task`…) of a module in the index. */
export interface IndexKindStats {
  entityType: string;
  documents: number;
}

export interface IndexModuleStats {
  module: string;
  documents: number;
  public: number;
  lastIndexedAt: string | null;
  /** Most documents first. */
  kinds: IndexKindStats[];
}

/** How far a module got with one kind of documents of a reindex it runs in chunks (chat messages, comments, tasks…). */
export interface ReindexProgress {
  module: string;
  kind: string;
  done: number;
  total: number;
  completed: boolean;
}

/**
 * The Ukrainian analysis of the index: `pending` documents wait for their keys (the background job `search.restem` works
 * through them in batches; "Run now" on the Jobs page).
 */
export interface IndexAnalysis {
  version: number;
  analyzed: number;
  pending: number;
}

export interface IndexStats {
  documents: number;
  tombstones: number;
  lastIndexedAt: string | null;
  modules: IndexModuleStats[];
  requests: {
    id: string;
    module: string | null;
    purge: boolean;
    requestedBy: UserRef | null;
    requestedAt: string;
    /** Per module and kind; empty for modules that republish in one go. */
    progress: ReindexProgress[];
  }[];
  analysis: IndexAnalysis;
}

export interface ReindexResult {
  id: string;
  module: string | null;
  purge: boolean;
  purged: number;
  requestedAt: string;
}

/** What the cleanup of unattached uploads does (`Files:Cleanup:Mode`, changeable here). */
export type CleanupMode = 'off' | 'dryRun' | 'live';

export interface FileCleanupDefaults {
  mode: CleanupMode;
  unattachedAgeDays: number;
  quarantineDays: number;
}

/** `GET /api/files/cleanup/settings`: what the job runs with (configuration plus the administrators' overrides). */
export interface FileCleanupSettings extends FileCleanupDefaults {
  /** An administrator overrode at least one value. */
  customized: boolean;
  defaults: FileCleanupDefaults;
  updatedAt: string | null;
  updatedBy: UserRef | null;
}

export interface FileGroup {
  files: number;
  bytes: number;
}

/** Files kept by one owner of claims: a module, or `internal`, `legacy`, `restored`. */
export interface FileClaimGroup extends FileGroup {
  owner: string;
}

export interface FileCleanupStats {
  /** Uploads nobody keeps (yet). */
  unattached: FileGroup;
  /** … of which older than the configured age: the next pass quarantines them. */
  eligible: FileGroup;
  quarantined: FileGroup;
  oldestUnattachedAt: string | null;
  nextPurgeAt: string | null;
  kept: FileClaimGroup[];
}

/** One recorded pass of the cleanup. */
export interface FileCleanupRun {
  id: string;
  trigger: 'job' | 'manual';
  dryRun: boolean;
  startedAt: string;
  finishedAt: string | null;
  startedBy: UserRef | null;
  candidates: number;
  candidateBytes: number;
  quarantined: number;
  quarantinedBytes: number;
  due: number;
  purged: number;
  purgedBytes: number;
  changed: number;
  failed: number;
  truncated: boolean;
  error: string | null;
}

export interface FileCleanupOverview {
  settings: FileCleanupSettings;
  stats: FileCleanupStats;
  runs: FileCleanupRun[];
  jobIntervalSeconds: number;
}

export interface UpdateFileCleanupSettings {
  mode: CleanupMode;
  unattachedAgeDays: number;
  quarantineDays: number;
}

export type CleanupAction = 'quarantined' | 'purged' | 'restored';

export interface FileCleanupSample {
  action: CleanupAction;
  fileId: string;
  name: string;
  size: number;
  owner: UserRef | null;
  uploadedAt: string;
}

/** `POST /api/files/cleanup/run`: what the pass did, or (a dry run) would do. */
export interface FileCleanupReport {
  ran: boolean;
  skippedReason: string | null;
  mode: CleanupMode;
  dryRun: boolean;
  candidates: number;
  candidateBytes: number;
  quarantined: number;
  quarantinedBytes: number;
  due: number;
  purged: number;
  purgedBytes: number;
  changed: number;
  failed: number;
  truncated: boolean;
  sample: FileCleanupSample[];
}

export interface QuarantinedFile {
  id: string;
  name: string;
  contentType: string;
  size: number;
  owner: UserRef | null;
  uploadedAt: string;
  quarantinedAt: string;
  purgeAt: string;
  /** Why it is in quarantine: nobody kept it (`unattached`) or the antivirus found malware in it (`infected`). */
  reason?: 'unattached' | 'infected';
  /** What the antivirus found (reason `infected`). */
  signature?: string | null;
}

// ---- Files: the antivirus (docs/modules/files.md "Antivirus scanning") ------------------------------------------------

/** What happens to files while the scanner (ClamAV) cannot be reached. */
export type ScannerDownPolicy = 'failOpen' | 'hold';

export interface FileScanDefaults {
  enabled: boolean;
  maxScanMegabytes: number;
  onUnavailable: ScannerDownPolicy;
}

/** `GET /api/files/scan/settings`: scanning on or off, the size limit and the policy (configuration plus the administrators' overrides). */
export interface FileScanSettings extends FileScanDefaults {
  customized: boolean;
  defaults: FileScanDefaults;
  updatedAt: string | null;
  updatedBy: UserRef | null;
}

export type UpdateFileScanSettings = FileScanDefaults;

export interface InfectedFile {
  id: string;
  name: string;
  contentType: string;
  size: number;
  /** Null for a visitor (a web form, the site widget, a messenger channel). */
  owner: UserRef | null;
  visitor: boolean;
  signature: string;
  uploadedAt: string;
  foundAt: string;
  claimedBy: string | null;
}

/** `GET /api/files/scan`: the antivirus at a glance. */
export interface FileScanOverview {
  host: string;
  settings: FileScanSettings;
  counts: { pending: number; clean: number; skipped: number; infected: number };
  /** Files waiting for a result for more than five minutes. */
  overdue: number;
  /** Files let through unscanned within the last hour because the scanner was down. */
  releasedUnscannedLastHour: number;
  infected: InfectedFile[];
}

/** `POST /api/files/scan/check`: the scanner's own answer. */
export interface ScannerCheck {
  reachable: boolean;
  version: string | null;
  error: string | null;
}

export interface FileCleanupLogEntry {
  id: string;
  runId: string | null;
  action: CleanupAction;
  fileId: string;
  name: string;
  contentType: string;
  size: number;
  owner: UserRef | null;
  uploadedAt: string;
  at: string;
  actor: UserRef | null;
}

// ---- Operations: recurring jobs, alerts, infrastructure settings, usage (docs/modules/admin.md) ---------------------------

export type JobRunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'skipped' | 'abandoned';
export type JobRunTrigger = 'schedule' | 'manual';

/** One run of a recurring job. */
export interface JobRun {
  id: string;
  job: string;
  trigger: JobRunTrigger;
  status: JobRunStatus;
  /** The administrator who pressed "Run now". */
  requestedBy: UserRef | null;
  requestedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  error: string | null;
  host: string | null;
}

/** A recurring job with how it has been running. */
export interface RecurringJob {
  /** `disk.trash-cleanup`. */
  name: string;
  /** The part before the first dot. */
  module: string;
  intervalSeconds: number;
  lastRun: JobRun | null;
  lastSuccessAt: string | null;
  /** Roughly when the scheduler runs it next; null while it has not run. */
  nextRunAt: string | null;
  /** A run is queued or in progress. */
  isRunning: boolean;
  /** Failed runs since the last success (the server counts up to 50). */
  consecutiveFailures: number;
  runs24h: number;
  failures24h: number;
}

/** `GET /api/admin/jobs`. */
export interface JobsOverview {
  jobs: RecurringJob[];
  /** Whether a worker process announced itself lately (without one nothing runs the jobs). */
  workerSeen: boolean;
  now: string;
}

export type AlertSeverity = 'warning' | 'critical';

/** The rules of the alert evaluator (`AlertRuleNames`). */
export const ALERT_RULES = [
  'error-queue',
  'outbox-parked',
  'outbox-lag',
  'job-failing',
  'http-5xx',
  'backup-age',
  'disk-space',
  'malware',
  'scanner',
] as const;
export type AlertRuleName = (typeof ALERT_RULES)[number];

/** An alert; the page words it from `rule`, `subject` and `params` in the viewer's language (`summary` is the portal language). */
export interface PortalAlert {
  id: string;
  rule: string;
  /** What it was found on (a job, an outbox schema, a disk path); empty when there is only one thing. */
  subject: string;
  severity: AlertSeverity;
  params: Record<string, string>;
  summary: string;
  firedAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  lastNotifiedAt: string | null;
  /** The first notice and the reminders. */
  notifications: number;
}

export interface AlertRule {
  rule: string;
  /** False when the portal cannot watch it (no backup marker is configured). */
  monitored: boolean;
  /** The limits in force, name → value. */
  thresholds: Record<string, string>;
}

/**
 * The notices about events of the audit journal (`Admin:AuditAlerts:*`): not conditions that open and resolve like the rules above, but
 * one-off notices to the administrators that appear (already resolved) among the recent alerts.
 */
export const AUDIT_ALERT_RULES = [
  'audit-admin-grant',
  'audit-export',
  'audit-bulk-delete',
  'audit-link-policy',
] as const;
export type AuditAlertRuleName = (typeof AUDIT_ALERT_RULES)[number];

/** Whether an alert is a notice about an audit event (`audit-…`). */
export const isAuditAlertRule = (rule: string): boolean =>
  (AUDIT_ALERT_RULES as readonly string[]).includes(rule);

export interface AuditAlertRule {
  rule: string;
  /** The notice is switched on (`Admin:AuditAlerts:Enabled` and the rule's own setting). */
  enabled: boolean;
  /** The limits in force, name → value (`rows`, `records`). */
  thresholds: Record<string, string>;
}

/** `GET /api/admin/alerts`. */
export interface AlertsOverview {
  enabled: boolean;
  active: PortalAlert[];
  recent: PortalAlert[];
  rules: AlertRule[];
  /** The notices about audit events and their limits; absent from an older API. */
  auditRules?: AuditAlertRule[];
  /** When the evaluator last finished a round; null if it never did (no worker). */
  evaluatedAt: string | null;
  renotifyAfterHours: number;
}

export type SettingKind =
  'text' | 'integer' | 'boolean' | 'choice' | 'secret' | 'minutes' | 'hours' | 'percent';
export type SettingGroup = 'smtp' | 'push' | 'rate-limits' | 'telemetry' | 'alerts';
/** Where the value in force comes from: this page, the environment (appsettings, `.env`) or the code's default. */
export type SettingSource = 'database' | 'environment' | 'default';

export interface InfrastructureSetting {
  /** The configuration key (`Email:Host`). */
  key: string;
  group: SettingGroup;
  kind: SettingKind;
  /** The value in force as shown (minutes and hours as numbers); null for a secret: it is never sent. */
  value: string | null;
  /** Some value is in force (whether a secret is set). */
  hasValue: boolean;
  source: SettingSource;
  /** What "Reset to environment" returns to (null for a secret). */
  fallbackValue: string | null;
  fallbackHasValue: boolean;
  choices: string[] | null;
  min: number | null;
  max: number | null;
  unit: 'minutes' | 'hours' | 'percent' | null;
  /** The running processes read it at startup: a change applies after a restart. */
  restartRequired: boolean;
  /** A stored secret that cannot be decrypted any more: enter it again. */
  unreadable: boolean;
  updatedAt: string | null;
  updatedBy: UserRef | null;
}

/** A message the SMTP server did not take. */
export interface EmailFailure {
  id: string;
  at: string;
  recipient: string;
  subject: string;
  error: string;
  server: string;
}

/** A STUN / TURN server of calls; its credentials are never sent. */
export interface IceServer {
  urls: string[];
  hasCredentials: boolean;
}

/** `GET /api/admin/infrastructure`. */
export interface Infrastructure {
  settings: InfrastructureSetting[];
  emailEnabled: boolean;
  /** The 20 latest e-mails the SMTP server refused or could not be reached for. */
  emailErrors: EmailFailure[];
  iceServers: IceServer[];
  /** `Admin__IgnoreStoredSettings` is on: nothing set on the page applies. */
  storedSettingsIgnored: boolean;
}

/** One change of `PUT /api/admin/infrastructure`: a null value resets the setting to the environment. */
export interface SettingChange {
  key: string;
  value: string | null;
}

export interface InfrastructureUpdateResult {
  infrastructure: Infrastructure;
  /** The changed settings that apply only after a restart. */
  restartRequired: string[];
}

export interface EmailTestResult {
  ok: boolean;
  error: string | null;
  server: string;
  durationMs: number;
}

export interface IceCheckResult {
  url: string;
  transport: 'udp' | 'tcp' | 'tls';
  ok: boolean;
  latencyMs: number | null;
  reflexiveAddress: string | null;
  error: string | null;
}

export interface VapidKeys {
  publicKey: string;
  privateKey: string;
}

/** `GET /api/admin/usage`: who uses the portal and how much it stores. */
export interface Usage {
  users: { total: number; activeDay: number; activeWeek: number; activeMonth: number };
  storage: {
    filesBytes: number;
    filesCount: number;
    databaseBytes: number;
    /** Files per keeping module, the biggest first; a null module is "other uploads". */
    byModule: { module: string | null; files: number; bytes: number }[];
  };
  /** The last 30 days, oldest first. */
  history: {
    day: string;
    activeDay: number;
    activeWeek: number;
    activeMonth: number;
    total: number;
    filesBytes: number;
    databaseBytes: number;
  }[];
  generatedAt: string;
}

/** Realtime: the portal settings changed (sent to every client). */
export const SETTINGS_UPDATED = 'admin.settings-updated';

export const adminKeys = {
  all: ['admin'] as const,
  publicSettings: ['admin', 'settings', 'public'] as const,
  settings: ['admin', 'settings', 'full'] as const,
  system: ['admin', 'system'] as const,
  messaging: ['admin', 'messaging', 'overview'] as const,
  summary: ['admin', 'messaging', 'summary'] as const,
  errors: (endpoint: string) => ['admin', 'messaging', 'errors', endpoint] as const,
  audit: (page: number) => ['admin', 'audit', page] as const,
  auditEntry: (id: string) => ['admin', 'audit', 'entry', id] as const,
  securityPolicy: ['admin', 'security-policy'] as const,
  index: ['admin', 'search-index'] as const,
  files: ['admin', 'files'] as const,
  filesOverview: ['admin', 'files', 'overview'] as const,
  diskPolicy: ['admin', 'disk', 'public-links'] as const,
  filesScan: ['admin', 'files', 'scan'] as const,
  filesQuarantine: (page: number) => ['admin', 'files', 'quarantine', page] as const,
  filesLog: (page: number) => ['admin', 'files', 'log', page] as const,
  jobs: ['admin', 'jobs', 'overview'] as const,
  jobsAll: ['admin', 'jobs'] as const,
  jobRuns: (job: string, page: number, pageSize: number, status: JobRunStatus | undefined) =>
    ['admin', 'jobs', 'runs', job, page, pageSize, status ?? 'all'] as const,
  alerts: ['admin', 'alerts'] as const,
  infrastructure: ['admin', 'infrastructure'] as const,
  usage: ['admin', 'usage'] as const,
};

/** The public settings (the shell's own query, same key): the page of the default menu reads the arrangement from them. */
export function usePublicPortalSettings() {
  return useQuery({
    queryKey: adminKeys.publicSettings,
    queryFn: ({ signal }) => api.get<PublicPortalSettings>('/admin/settings/public', { auth: false, signal }),
    staleTime: 5 * 60_000,
  });
}

export function useAdminSettings(enabled = true) {
  return useQuery({
    queryKey: adminKeys.settings,
    queryFn: ({ signal }) => api.get<PortalSettings>('/admin/settings', { signal }),
    enabled,
  });
}

export function useUpdatePortalSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdatePortalSettings) => api.put<PortalSettings>('/admin/settings', request),
    meta: { silent: true },
    onSuccess: (settings) => {
      client.setQueryData(adminKeys.settings, settings);
      // The shell applies name, logo and accent from the public settings at once.
      client.setQueryData<PublicPortalSettings>(adminKeys.publicSettings, (previous) => ({
        companyName: settings.companyName,
        logoUrl: settings.logoUrl,
        accentColor: settings.accentColor,
        defaultLanguage: settings.defaultLanguage,
        defaultTimeZone: settings.defaultTimeZone,
        menu: previous?.menu ?? null,
      }));
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

/**
 * Saves the default arrangement of the left menu for everybody (`null`: back to the natural order); the answer is the public
 * settings, which the shell reads, so every open tab follows (the server also pushes `admin.settings-updated`).
 */
export function useSaveDefaultMenu() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (layout: MenuLayout | null) =>
      layout
        ? api.put<PublicPortalSettings>('/admin/settings/menu', layout)
        : api.delete<PublicPortalSettings>('/admin/settings/menu'),
    meta: { silent: true },
    onSuccess: (settings) => {
      client.setQueryData<PublicPortalSettings>(adminKeys.publicSettings, settings);
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useSystemInfo(enabled = true) {
  return useQuery({
    queryKey: adminKeys.system,
    queryFn: ({ signal }) => api.get<SystemInfo>('/admin/system', { signal }),
    refetchInterval: 30_000,
    enabled,
  });
}

export function useMessagingOverview(autoRefresh: boolean, enabled = true) {
  return useQuery({
    queryKey: adminKeys.messaging,
    queryFn: ({ signal }) => api.get<MessagingOverview>('/admin/messaging', { signal }),
    refetchInterval: autoRefresh ? 5_000 : false,
    enabled,
  });
}

export function useMessagingSummary(enabled = true) {
  return useQuery({
    queryKey: adminKeys.summary,
    queryFn: ({ signal }) => api.get<MessagingSummary>('/admin/messaging/summary', { signal }),
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled,
  });
}

export function useErrorMessages(endpoint: string) {
  return useQuery({
    queryKey: adminKeys.errors(endpoint),
    queryFn: ({ signal }) =>
      api.get<ErrorMessage[]>(`/admin/messaging/endpoints/${encodeURIComponent(endpoint)}/errors`, {
        query: { limit: 100 },
        signal,
      }),
  });
}

function invalidateMessaging(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['admin', 'messaging'] });
  void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
}

export function useRequeueMessage(endpoint: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.post(
        `/admin/messaging/endpoints/${encodeURIComponent(endpoint)}/errors/${encodeURIComponent(messageId)}/requeue`,
      ),
    onMutate: async (messageId) => {
      // Optimistic: the message leaves the error queue.
      await client.cancelQueries({ queryKey: adminKeys.errors(endpoint) });
      const saved = client.getQueryData<ErrorMessage[]>(adminKeys.errors(endpoint));
      client.setQueryData<ErrorMessage[]>(adminKeys.errors(endpoint), (list) =>
        list?.filter((m) => m.messageId !== messageId),
      );
      return saved;
    },
    onError: (_error, _id, saved) => client.setQueryData(adminKeys.errors(endpoint), saved),
    onSettled: () => invalidateMessaging(client),
  });
}

export function useRequeueAll(endpoint: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ count: number }>(
        `/admin/messaging/endpoints/${encodeURIComponent(endpoint)}/errors/requeue-all`,
      ),
    onSettled: () => invalidateMessaging(client),
  });
}

export function usePurgeErrors(endpoint: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.delete<{ count: number }>(`/admin/messaging/endpoints/${encodeURIComponent(endpoint)}/errors`),
    onSettled: () => invalidateMessaging(client),
  });
}

export function useRequeueParked() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (schema: string | null) =>
      api.post<{ count: number }>('/admin/messaging/outbox/requeue-parked', { schema }),
    onSettled: () => invalidateMessaging(client),
  });
}

export function useAudit(page: number, pageSize = 10, filter: AuditFilter = {}, enabled = true) {
  return useQuery({
    enabled,
    queryKey: [...adminKeys.audit(page), pageSize, filter],
    queryFn: ({ signal }) =>
      api.get<PagedResult<AuditEntry>>('/admin/audit', {
        query: {
          page,
          pageSize,
          category: filter.category,
          action: filter.actions,
          userId: filter.userId,
          from: filter.from,
          to: filter.to,
          q: filter.q,
        },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** One entry of the journal (the record card); `initial` is the row the list already has. */
export function useAuditEntry(id: string | null, initial?: AuditEntry) {
  return useQuery({
    queryKey: adminKeys.auditEntry(id ?? ''),
    queryFn: ({ signal }) => api.get<AuditEntry>(`/admin/audit/${encodeURIComponent(id ?? '')}`, { signal }),
    enabled: !!id,
    placeholderData: initial,
  });
}

/** The file name in a `Content-Disposition` header (`filename="…"` or `filename*=UTF-8''…`). */
export function fileNameOf(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=utf-8''([^;]+)/iu.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      /* the plain name below */
    }
  }
  return /filename="?([^";]+)"?/iu.exec(disposition)?.[1] ?? null;
}

/** The byte order mark Excel needs to read Cyrillic in a UTF-8 CSV file. */
const BYTE_ORDER_MARK = String.fromCodePoint(0xfeff);

/** The journal under the filter as a CSV file (`GET /api/admin/audit/export`, at most `AUDIT_EXPORT_LIMIT` entries). */
export async function exportAudit(filter: AuditFilter): Promise<AuditExport> {
  const seen: { truncated: boolean; rows: number | null; fileName: string | null } = {
    truncated: false,
    rows: null,
    fileName: null,
  };
  const body = await api.get<Blob | string>('/admin/audit/export', {
    query: {
      category: filter.category,
      action: filter.actions,
      userId: filter.userId,
      from: filter.from,
      to: filter.to,
      q: filter.q,
    },
    headers: { Accept: '*/*' },
    onResponse: (response) => {
      seen.truncated = response.headers.get('X-Orbita-Truncated') === 'true';
      const header = response.headers.get('X-Orbita-Rows');
      seen.rows = header !== null && Number.isFinite(Number(header)) ? Number(header) : null;
      seen.fileName = fileNameOf(response.headers.get('Content-Disposition'));
    },
  });
  // Decoding the text dropped the byte order mark: put it back.
  const blob =
    typeof body === 'string' ? new Blob([BYTE_ORDER_MARK, body], { type: 'text/csv;charset=utf-8' }) : body;
  return { blob, fileName: seen.fileName ?? 'orbita-audit.csv', truncated: seen.truncated, rows: seen.rows };
}

export function useSecurityPolicy() {
  return useQuery({
    queryKey: adminKeys.securityPolicy,
    queryFn: ({ signal }) => api.get<SecurityPolicy>('/auth/security-policy', { signal }),
  });
}

export function useUpdateSecurityPolicy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SecurityPolicyInput) => api.put<SecurityPolicy>('/auth/security-policy', input),
    meta: { silent: true },
    onSuccess: (policy) => {
      client.setQueryData(adminKeys.securityPolicy, policy);
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useIndexStats(enabled = true) {
  return useQuery({
    queryKey: adminKeys.index,
    queryFn: ({ signal }) => api.get<IndexStats>('/search/stats', { signal }),
    // Faster while a reindex or the Ukrainian analysis is running (the page shows its progress).
    refetchInterval: (query) => {
      const stats = query.state.data;
      const recent = (requestedAt: string) => Date.now() - Date.parse(requestedAt) < 3_600_000;
      const working =
        !!stats &&
        (stats.analysis.pending > 0 ||
          stats.requests.some((r) => recent(r.requestedAt) && r.progress.some((p) => !p.completed)));
      return working ? 4_000 : 15_000;
    },
    enabled,
  });
}

export function useReindex() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: { module: string | null; purge: boolean }) =>
      api.post<ReindexResult>('/search/reindex', request),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: adminKeys.index });
      void client.invalidateQueries({ queryKey: ['search'] });
    },
  });
}

// ---- Disk: who may publish files through public links (docs/modules/disk.md "Public links") ----------------------------

/** `all`: everyone who manages an item; `admins`: administrators only; `off`: no public links (existing ones stop working). */
export type PublicLinkPolicyKind = 'all' | 'admins' | 'off';

/** `GET/PUT /api/disk/settings/public-links`. */
export interface PublicLinkPolicy {
  publicLinks: PublicLinkPolicyKind;
  /** Whether the caller may create or change public links under the policy. */
  canPublish: boolean;
  updatedAt?: string | null;
  updatedBy?: UserRef | null;
}

export function useDiskPublicLinkPolicy() {
  return useQuery({
    queryKey: adminKeys.diskPolicy,
    queryFn: ({ signal }) => api.get<PublicLinkPolicy>('/disk/settings/public-links', { signal }),
  });
}

export function useSetDiskPublicLinkPolicy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (publicLinks: PublicLinkPolicyKind) =>
      api.put<PublicLinkPolicy>('/disk/settings/public-links', { publicLinks }),
    onSuccess: (policy) => {
      client.setQueryData(adminKeys.diskPolicy, policy);
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
      void client.invalidateQueries({ queryKey: ['disk', 'public-link-policy'] });
    },
  });
}

// ---- Files: cleanup of unattached uploads (docs/modules/files.md) ------------------------------------------------------

export function useFilesCleanup() {
  return useQuery({
    queryKey: adminKeys.filesOverview,
    queryFn: ({ signal }) => api.get<FileCleanupOverview>('/files/cleanup', { signal }),
    refetchInterval: 30_000,
  });
}

export function useQuarantinedFiles(page: number, pageSize = 10) {
  return useQuery({
    queryKey: [...adminKeys.filesQuarantine(page), pageSize],
    queryFn: ({ signal }) =>
      api.get<PagedResult<QuarantinedFile>>('/files/cleanup/quarantine', {
        query: { page, pageSize },
        signal,
      }),
  });
}

export function useFilesCleanupLog(page: number, pageSize = 10) {
  return useQuery({
    queryKey: [...adminKeys.filesLog(page), pageSize],
    queryFn: ({ signal }) =>
      api.get<PagedResult<FileCleanupLogEntry>>('/files/cleanup/log', {
        query: { page, pageSize },
        signal,
      }),
  });
}

function invalidateFiles(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: adminKeys.files });
}

export function useFilesScan() {
  return useQuery({
    queryKey: adminKeys.filesScan,
    queryFn: ({ signal }) => api.get<FileScanOverview>('/files/scan', { signal }),
    refetchInterval: 30_000,
  });
}

export function useUpdateFilesScanSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateFileScanSettings) =>
      api.put<FileScanSettings>('/files/scan/settings', request),
    meta: { silent: true },
    onSettled: () => {
      invalidateFiles(client);
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useResetFilesScanSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<FileScanSettings>('/files/scan/settings'),
    onSettled: () => invalidateFiles(client),
  });
}

export function useCheckFileScanner() {
  return useMutation({
    mutationFn: () => api.post<ScannerCheck>('/files/scan/check'),
    meta: { silent: true },
  });
}

export function useDeleteInfectedFile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => api.delete(`/files/scan/infected/${encodeURIComponent(fileId)}`),
    onSettled: () => invalidateFiles(client),
  });
}

export function useReleaseInfectedFile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => api.post(`/files/scan/infected/${encodeURIComponent(fileId)}/release`),
    onSettled: () => invalidateFiles(client),
  });
}

export function useUpdateFilesCleanupSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateFileCleanupSettings) =>
      api.put<FileCleanupSettings>('/files/cleanup/settings', request),
    meta: { silent: true },
    onSettled: () => invalidateFiles(client),
  });
}

export function useResetFilesCleanupSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<FileCleanupSettings>('/files/cleanup/settings'),
    onSettled: () => invalidateFiles(client),
  });
}

export function useRunFilesCleanup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dryRun: boolean) => api.post<FileCleanupReport>('/files/cleanup/run', { dryRun }),
    onSettled: () => invalidateFiles(client),
  });
}

export function useRestoreQuarantinedFile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      api.post(`/files/cleanup/quarantine/${encodeURIComponent(fileId)}/restore`),
    onSettled: () => invalidateFiles(client),
  });
}

export function useRestoreAllQuarantinedFiles() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ restored: number }>('/files/cleanup/quarantine/restore'),
    onSettled: () => invalidateFiles(client),
  });
}

// ---- Operations: recurring jobs ------------------------------------------------------------------------------------------

const isRunActive = (run: JobRun | null | undefined) => run?.status === 'queued' || run?.status === 'running';

/** The jobs page polls every 5 seconds while a job runs or waits to run (so "Run now" shows its progress), else every 15. */
export function useJobs() {
  return useQuery({
    queryKey: adminKeys.jobs,
    queryFn: ({ signal }) => api.get<JobsOverview>('/admin/jobs', { signal }),
    refetchInterval: (query) =>
      query.state.data?.jobs.some((job) => job.isRunning || isRunActive(job.lastRun)) ? 5_000 : 15_000,
    staleTime: 0,
  });
}

/** The history of a job's runs, newest first; polls quickly while a listed run is queued or running. */
export function useJobRuns(
  job: string,
  page: number,
  options: { status?: JobRunStatus; pageSize?: number; enabled?: boolean } = {},
) {
  const { status, pageSize = 10, enabled = true } = options;
  return useQuery({
    queryKey: adminKeys.jobRuns(job, page, pageSize, status),
    queryFn: ({ signal }) =>
      api.get<PagedResult<JobRun>>(`/admin/jobs/${encodeURIComponent(job)}/runs`, {
        query: { page, pageSize, status },
        signal,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.items.some(isRunActive) ? 3_000 : 30_000),
    staleTime: 0,
    enabled,
  });
}

/** "Run now": 202 with the waiting run, 409 `admin.job_already_running`, 404 `admin.job_not_found`. */
export function useRunJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (job: string) => api.post<JobRun>(`/admin/jobs/${encodeURIComponent(job)}/run`),
    meta: { silent: true },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: adminKeys.jobsAll });
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

// ---- Operations: alerts --------------------------------------------------------------------------------------------------

export function useAlerts(enabled = true) {
  return useQuery({
    queryKey: adminKeys.alerts,
    queryFn: ({ signal }) => api.get<AlertsOverview>('/admin/alerts', { signal }),
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled,
  });
}

// ---- Operations: infrastructure settings ---------------------------------------------------------------------------------

export function useInfrastructure() {
  return useQuery({
    queryKey: adminKeys.infrastructure,
    queryFn: ({ signal }) => api.get<Infrastructure>('/admin/infrastructure', { signal }),
  });
}

/** Saves the changed settings (a null value resets one to the environment). Validation problems come back as field errors. */
export function useUpdateInfrastructure() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (changes: SettingChange[]) =>
      api.put<InfrastructureUpdateResult>('/admin/infrastructure', { changes }),
    meta: { silent: true },
    onSuccess: (result) => {
      client.setQueryData(adminKeys.infrastructure, result.infrastructure);
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
      void client.invalidateQueries({ queryKey: adminKeys.alerts });
    },
  });
}

export function useSendTestEmail() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (to: string) => api.post<EmailTestResult>('/admin/infrastructure/email/test', { to }),
    meta: { silent: true },
    onSettled: () => {
      // A failed send is also in the list of the latest send errors; the test is in the audit journal.
      void client.invalidateQueries({ queryKey: adminKeys.infrastructure });
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useCheckIceServers() {
  return useMutation({
    mutationFn: () =>
      api.post<{ results: IceCheckResult[] }>('/admin/infrastructure/ice/check').then((r) => r.results),
  });
}

/** A new web push (VAPID) key pair; nothing is stored until the settings are saved. */
export function useGenerateVapidKeys() {
  return useMutation({
    mutationFn: () => api.post<VapidKeys>('/admin/infrastructure/push/keys'),
  });
}

// ---- Operations: usage -----------------------------------------------------------------------------------------------------

export function useUsage(enabled = true) {
  return useQuery({
    queryKey: adminKeys.usage,
    queryFn: ({ signal }) => api.get<Usage>('/admin/usage', { signal }),
    refetchInterval: 60_000,
    enabled,
  });
}

// ---- History of field changes (docs/modules/history.md) -----------------------------------------------------------------

/** A field the administrator switched off for a kind of records. */
export interface HistoryFieldSwitch {
  entityType: string;
  fieldCode: string;
}

/** `GET /api/history/settings`. */
export interface HistorySettings {
  enabled: boolean;
  /** A history database is configured on the server. */
  configured: boolean;
  /** Recorded now: enabled and configured. */
  effective: boolean;
  retentionDays: number;
  disabledEntityTypes: string[];
  disabledFields: HistoryFieldSwitch[];
  /** The kinds of records modules report history for (plus the switched-off ones). */
  knownEntityTypes: string[];
  minRetentionDays: number;
  maxRetentionDays: number;
  defaultRetentionDays: number;
  updatedAt: string | null;
  updatedBy: UserRef | null;
}

/** `PUT /api/history/settings` replaces the settings. */
export interface UpdateHistorySettings {
  enabled: boolean;
  retentionDays: number;
  disabledEntityTypes: string[];
  disabledFields: HistoryFieldSwitch[];
}

/** `GET /api/history/status`. */
export interface HistoryStatus {
  configured: boolean;
  enabled: boolean;
  reachable: boolean;
  migrated: boolean;
  sizeBytes: number | null;
  oldestAt: string | null;
  newestAt: string | null;
  changeSetsEstimate: number | null;
  /** How far the modules' outboxes lag behind, in seconds (0 = nothing waits). */
  lagSeconds: number;
  retentionDays: number;
  lastPurgeAt: string | null;
  lastPurgeChangeSets: number | null;
}

const historyKeys = {
  settings: ['admin', 'history', 'settings'] as const,
  status: ['admin', 'history', 'status'] as const,
};

export function useHistorySettings() {
  return useQuery({
    queryKey: historyKeys.settings,
    queryFn: ({ signal }) => api.get<HistorySettings>('/history/settings', { signal }),
  });
}

export function useUpdateHistorySettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (settings: UpdateHistorySettings) => api.put<HistorySettings>('/history/settings', settings),
    onSuccess: (settings) => {
      client.setQueryData(historyKeys.settings, settings);
      void client.invalidateQueries({ queryKey: historyKeys.status });
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}

export function useHistoryStatus() {
  return useQuery({
    queryKey: historyKeys.status,
    queryFn: ({ signal }) => api.get<HistoryStatus>('/history/status', { signal }),
    refetchInterval: 30_000,
  });
}

/** Deletes the whole history; the server asks for the explicit confirmation and audits it. */
export function useClearHistory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ changeSets: number }>('/history/clear', { confirm: true }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: historyKeys.status });
      void client.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });
}
